import { Link } from "react-router-dom";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import SiteFooter from "@/components/site/SiteFooter";
import useReveal from "@/hooks/useReveal";

// Live Stripe Payment Link for GushPay Access ($225/mo).
// To change: create a new Payment Link in Stripe Dashboard (Live mode),
// copy the URL, and replace below.
export const CHECKOUT_URL =
  "https://buy.stripe.com/28E9AT52E9nP20wc0C0ZW03";

const PLAN = {
  id: "access",
  name: "GushPay Access",
  price: "$225",
  cadence: "/ month",
  tagline: "One flat price. Every operator. Every well.",
  features: [
    "Unlimited active wells",
    "Smart cost coding",
    "Automated Joint Interest Billing (JIB)",
    "Dynamic Lease Operating Statements (LOS)",
    "Automated revenue distributions",
    "ORRI & royalty check generation",
    "Direct ACH integration",
    "Standard email support",
  ],
  cta: "Start Your Free Trial",
  microcopy: "Instant · self-serve · cancel anytime",
};

function isTestMode(url) {
  return url.includes("/test_");
}

export default function Pricing() {
  const headerRef = useReveal();
  const cardRef = useReveal();
  const guaranteeRef = useReveal();

  const handleCheckout = () => {
    window.open(CHECKOUT_URL, "_blank", "noopener,noreferrer");
  };

  const testMode = isTestMode(CHECKOUT_URL);

  return (
    <div
      className="min-h-screen bg-[#F4F3EE] text-[#111418]"
      data-testid="pricing-page"
    >
      <Navbar />

      <main>
        {/* Header */}
        <section className="pt-32 md:pt-40 pb-16 md:pb-20" data-testid="pricing-header">
          <div className="gp-container">
            <div ref={headerRef} className="reveal max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <span className="h-px w-8 bg-[#D67229]" aria-hidden />
                <span className="gp-overline">// Pricing</span>
                <span className="h-px w-8 bg-[#D67229]" aria-hidden />
              </div>
              <h1
                data-testid="pricing-headline"
                className="font-display text-[2.6rem] sm:text-6xl lg:text-7xl xl:text-[5rem] font-bold tracking-tight text-[#111418] leading-[1.02]"
              >
                One flat price.{" "}
                <span className="italic font-normal text-[#0F4C5C]">
                  Every operator.
                </span>
              </h1>
              <p
                data-testid="pricing-subheadline"
                className="mt-8 max-w-2xl mx-auto text-lg text-[#2C333A] leading-relaxed"
              >
                No massive setup fees. No long-term contracts. No sales calls.
                Pick your tier, enter your wells, and you&#39;re live.
              </p>
              <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C333A]">
                <span>· Self-serve · 60-sec signup</span>
                <span>· Cancel or pause anytime</span>
                <span>· Zero installation fees</span>
              </div>
            </div>
          </div>
        </section>

        {/* Single flat-rate card */}
        <section className="pb-16 md:pb-24" data-testid="pricing-plan">
          <div className="gp-container">
            <article
              ref={cardRef}
              data-testid="pricing-plan-card"
              className="reveal relative max-w-3xl mx-auto bg-[#082933] text-[#F4F3EE] border border-[#082933] shadow-[16px_16px_0_0_#D67229] overflow-hidden"
            >
              <span
                className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-[#D67229] text-white font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1"
                data-testid="pricing-flat-badge"
              >
                <Sparkles size={11} strokeWidth={2} /> Flat-rate access
              </span>

              <div className="gp-grain" aria-hidden />

              <div className="relative p-8 md:p-12 lg:p-14">
                <div className="flex items-baseline gap-4 flex-wrap">
                  <span
                    className="gp-overline"
                    style={{ color: "#E88B46" }}
                  >
                    01 · {PLAN.name}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#F4F3EE]/60">
                    Everything · every well
                  </span>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end">
                  <div className="md:col-span-6">
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-[#F4F3EE] leading-[1.05]">
                      GushPay <span className="italic text-[#E88B46]">Access</span>
                    </h2>
                    <p className="mt-4 text-[#F4F3EE]/80 leading-relaxed max-w-md">
                      {PLAN.tagline} Everything GushPay does — one price, no
                      well-count math.
                    </p>
                  </div>
                  <div className="md:col-span-6 md:text-right">
                    <p
                      data-testid="pricing-plan-price"
                      className="font-display text-6xl md:text-7xl text-[#E88B46] leading-none"
                    >
                      {PLAN.price}
                    </p>
                    <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-[#F4F3EE]/60">
                      {PLAN.cadence} · flat rate
                    </p>
                  </div>
                </div>

                <div className="mt-10 border-t border-[#F4F3EE]/15 pt-8">
                  <p
                    className="font-mono text-[11px] uppercase tracking-[0.2em] mb-5"
                    style={{ color: "#E88B46" }}
                  >
                    // Everything included
                  </p>
                  <ul
                    className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3"
                    data-testid="pricing-plan-features"
                  >
                    {PLAN.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <span
                          aria-hidden
                          className="mt-1 inline-flex h-4 w-4 items-center justify-center bg-[#E88B46] text-[#082933] flex-shrink-0"
                        >
                          <Check size={11} strokeWidth={3} />
                        </span>
                        <span className="text-[15px] text-[#F4F3EE] leading-relaxed">
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="button"
                    onClick={handleCheckout}
                    data-testid="pricing-plan-cta"
                    className="inline-flex items-center justify-center gap-2 h-12 px-8 bg-[#E88B46] text-[#082933] hover:bg-[#F4F3EE] font-sans font-medium uppercase tracking-[0.14em] text-[0.78rem] transition-colors"
                  >
                    {PLAN.cta}
                    <ArrowUpRight size={16} />
                  </button>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#F4F3EE]/65">
                    {PLAN.microcopy}
                  </span>
                </div>

                {testMode && (
                  <div
                    data-testid="pricing-test-mode-warning"
                    className="mt-8 border border-[#E88B46]/60 bg-[#E88B46]/10 px-4 py-3 flex items-start gap-3"
                  >
                    <span
                      aria-hidden
                      className="mt-0.5 inline-block h-2 w-2 bg-[#E88B46] flex-shrink-0"
                    />
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4F3EE]/85 leading-relaxed">
                      Dev notice · Stripe link is in TEST mode. Swap for a
                      live-mode payment link before real customers can pay.
                    </p>
                  </div>
                )}
              </div>
            </article>
          </div>
        </section>

        {/* Guarantee */}
        <section
          className="pb-24 md:pb-32"
          data-testid="pricing-guarantee-section"
        >
          <div className="gp-container">
            <div
              ref={guaranteeRef}
              className="reveal relative max-w-5xl mx-auto bg-[#EBE9E1] border-l-4 border-[#D67229] p-8 md:p-12"
            >
              <span className="gp-overline">// The GushPay guarantee</span>
              <p
                data-testid="pricing-guarantee-text"
                className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl text-[#111418] leading-[1.2]"
              >
                Unlike legacy software that charges{" "}
                <span className="italic text-[#7A3906]">
                  tens of thousands upfront
                </span>{" "}
                for installation and training, GushPay is completely
                self-serve with{" "}
                <span className="text-[#0F4C5C]">zero hidden fees</span>. Pause
                or cancel anytime.
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-[#D3D9DE] pt-6">
                {[
                  { k: "$0", v: "Setup fee" },
                  { k: "0", v: "Long-term contracts" },
                  { k: "Anytime", v: "Pause or cancel" },
                ].map((item) => (
                  <div key={item.k}>
                    <p className="font-display text-2xl md:text-3xl text-[#0F4C5C]">
                      {item.k}
                    </p>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-[#2C333A]">
                      {item.v}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12 text-center">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C333A]">
                · Have questions?{" "}
                <Link
                  to="/"
                  data-testid="pricing-back-link"
                  className="text-[#D67229] hover:text-[#0F4C5C] transition-colors underline underline-offset-4"
                >
                  See how GushPay works
                </Link>
              </p>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
